// framework/client/core/PushManager.js
import { ComponentBase } from "./ComponentBase.js";

export class PushManager extends ComponentBase {
    constructor(config) {
        super(config);
        this.vapidPublicKey = null;
        this.isSubscribed = false;
        this.swRegistration = null;
        this.isSupported = false;
    }

    async initialize() {
        // Check for browser support
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            this.log.warn("Push messaging is not supported by this browser.");
            this.isSupported = false;
            return;
        }

        // Check if there's a service worker registration
        try {
            const existingRegistration = await navigator.serviceWorker.getRegistration();
            
            if (!existingRegistration) {
                this.log.info("No service worker registration found. Push notifications are disabled.");
                this.isSupported = false;
                return;
            }

            // Wait for the service worker to be ready with a timeout
            this.swRegistration = await Promise.race([
                navigator.serviceWorker.ready,
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Service worker ready timeout')), 5000)
                )
            ]);

            this.isSupported = true;
            this.log.info("Service worker ready for push notifications.");

        } catch (error) {
            this.log.warn("Service worker initialization failed or timed out:", error.message);
            this.isSupported = false;
            return;
        }

        // Fetch the VAPID public key from the server
        try {
            const response = await this.appClient.rpcClient.call('PushNotificationRpc.getVapidPublicKey');
            this.vapidPublicKey = response.publicKey;
        } catch (error) {
            this.log.error("Failed to fetch VAPID public key from server.", error);
            this.isSupported = false;
            return;
        }

        // Check the initial subscription state
        await this.updateSubscriptionState();
    }

    /**
     * Checks the current push subscription status and updates the internal state.
     */
    async updateSubscriptionState() {
        if (!this.isSupported || !this.swRegistration) {
            this.isSubscribed = false;
            return false;
        }

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            this.isSubscribed = !(subscription === null);
            return this.isSubscribed;
        } catch (error) {
            this.log.error("Error checking subscription state:", error);
            this.isSubscribed = false;
            return false;
        }
    }

    /**
     * Subscribes the user to push notifications.
     * It handles the permission prompt and sends the subscription to the server.
     */
    async subscribe() {
        if (!this.isSupported) {
            this.log.warn("Cannot subscribe. Push notifications are not supported or enabled.");
            this.appClient.notifier?.warn("Push notifications are not available.");
            return;
        }

        if (!this.vapidPublicKey || !this.swRegistration) {
            this.log.error("Cannot subscribe. PushManager not properly initialized.");
            return;
        }

        // Check if already subscribed
        if (await this.updateSubscriptionState()) {
            this.log.info("User is already subscribed to push notifications.");
            return;
        }

        try {
            const subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
            });

            this.log.info("User subscribed successfully. Sending to server...");
            await this.appClient.rpcClient.call('PushNotificationRpc.subscribe', {
                subscriptionObject: subscription.toJSON()
            });

            this.isSubscribed = true;
            this.log.info("Push subscription successfully registered with the server.");

        } catch (error) {
            this.log.error("Failed to subscribe user for push notifications:", error);
            if (Notification.permission === 'denied') {
                this.appClient.notifier?.warn("Push notifications are blocked. Please enable them in your browser settings.");
            }
        }
    }

    /**
     * Unsubscribes the user from push notifications.
     */
    async unsubscribe() {
        if (!this.isSupported || !this.swRegistration) {
            this.log.warn("Cannot unsubscribe. Push notifications are not supported or enabled.");
            return;
        }

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            if (subscription) {
                await this.appClient.rpcClient.call('PushNotificationRpc.unsubscribe', {
                    subscriptionObject: subscription.toJSON()
                });

                const unsubscribedSuccessfully = await subscription.unsubscribe();
                if (unsubscribedSuccessfully) {
                    this.isSubscribed = false;
                    this.log.info("User unsubscribed successfully from both server and browser.");
                }
            }
        } catch (error) {
            this.log.error("Error unsubscribing from push notifications:", error);
        }
    }

    /**
     * Helper function to convert VAPID key.
     */
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}